import { useState, useCallback, useRef } from 'react';

const MAX_HISTORY = 50;

/**
 * Hook that provides undo/redo for ReactFlow nodes and edges.
 *
 * Usage:
 *   const { pushState, undo, redo, canUndo, canRedo } = useUndoRedo();
 *
 *   // Call pushState(nodes, edges) after each meaningful user action.
 */
const useUndoRedo = () => {
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const skipRef = useRef(false); // prevent recursive pushes during undo/redo

  const pushState = useCallback((nodes, edges) => {
    if (skipRef.current) return;
    setPast((prev) => {
      const next = [...prev, { nodes: structuredClone(nodes), edges: structuredClone(edges) }];
      return next.length > MAX_HISTORY ? next.slice(-MAX_HISTORY) : next;
    });
    setFuture([]);
  }, []);

  const undo = useCallback((currentNodes, currentEdges, setNodes, setEdges) => {
    setPast((prev) => {
      if (prev.length === 0) return prev;
      const newPast = [...prev];
      const last = newPast.pop();

      setFuture((f) => [
        ...f,
        { nodes: structuredClone(currentNodes), edges: structuredClone(currentEdges) },
      ]);

      skipRef.current = true;
      setNodes(last.nodes);
      setEdges(last.edges);
      requestAnimationFrame(() => { skipRef.current = false; });

      return newPast;
    });
  }, []);

  const redo = useCallback((currentNodes, currentEdges, setNodes, setEdges) => {
    setFuture((prev) => {
      if (prev.length === 0) return prev;
      const newFuture = [...prev];
      const next = newFuture.pop();

      setPast((p) => [
        ...p,
        { nodes: structuredClone(currentNodes), edges: structuredClone(currentEdges) },
      ]);

      skipRef.current = true;
      setNodes(next.nodes);
      setEdges(next.edges);
      requestAnimationFrame(() => { skipRef.current = false; });

      return newFuture;
    });
  }, []);

  return {
    pushState,
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
  };
};

export default useUndoRedo;
